import * as THREE from 'three'

import Experience from '../Experience'
import {
  parseMdl,
  sampleBoneLocals,
  findSequence,
  type BoneLocal,
  type Mdl,
} from '@/cs/mdl'
import {
  buildMdlMesh,
  computeBoneWorld,
  angleQuaternion,
  type MdlBuilt,
} from '@/cs/buildMdlMesh'
import type { WeaponKind } from './Weapon'
export type { WeaponKind } from './Weapon'

const MODEL_URL = '/cs/models/leet.mdl'
// GoldSrc models face +X; rotate so the body faces the player's look direction.
const FACE_OFFSET = Math.PI / 2
const ONE = new THREE.Vector3(1, 1, 1)
let sharedLightsAdded = false

const DEFAULT_AIM_SET: AimSet = {
  aim: 0,
  shoot: 0,
  reload: 0,
  crouchAim: 0,
  crouchShoot: 0,
  crouchReload: 0,
}
const DEFAULT_KNIFE_AIM_SET: AimSet = {
  aim: 0,
  shoot: 0,
  reload: -1,
  crouchAim: 0,
  crouchShoot: 0,
  crouchReload: -1,
}

/** A bone-merged held weapon (p_*.mdl): shared bones reuse the player's matrices. */
interface MergedWeapon {
  url: string
  mdl?: Mdl
  built?: MdlBuilt
  boneMap: number[] // gun bone → player bone index (-1 if local)
  locals: BoneLocal[]
}

/** Per-weapon upper-body sequences (the held-pose / fire / reload). */
interface AimSet {
  aim: number
  shoot: number
  reload: number // -1 for melee
  crouchAim: number
  crouchShoot: number
  crouchReload: number // -1 for melee
}

/**
 * Third-person player body: the real CS 1.6 Terrorist (leet.mdl), skeletally
 * animated (idle / walk / run by speed) and posed in place. The held weapon is
 * a bone-merged p_*.mdl that can be swapped (AK ↔ knife). Shown only in 3rd
 * person.
 */
export default class PlayerModel {
  public readonly group = new THREE.Group()
  private readonly inner = new THREE.Group()

  private mdl?: Mdl
  private built?: MdlBuilt
  private footBones: number[] = []
  private gait = { idle: 0, walk: 0, run: 0, crouchIdle: 0, crouchWalk: 0 }
  private deaths: number[] = []
  private aimSets: Record<WeaponKind, AimSet> = buildDefaultAimSets()
  private upperBody: boolean[] = [] // torso/arms use the aim pose, legs the gait

  private readonly weapons: Record<WeaponKind, MergedWeapon> = {
    knife: { url: '/cs/models/p_knife.mdl', boneMap: [], locals: [] },
    ak: { url: '/cs/models/p_ak47.mdl', boneMap: [], locals: [] },
    mp5: { url: '/cs/models/p_mp5.mdl', boneMap: [], locals: [] },
    tmp: { url: '/cs/models/p_tmp.mdl', boneMap: [], locals: [] },
    p90: { url: '/cs/models/p_p90.mdl', boneMap: [], locals: [] },
    mac10: { url: '/cs/models/p_mac10.mdl', boneMap: [], locals: [] },
    m4a1: { url: '/cs/models/p_m4a1.mdl', boneMap: [], locals: [] },
    m3: { url: '/cs/models/p_m3.mdl', boneMap: [], locals: [] },
    xm1014: { url: '/cs/models/p_xm1014.mdl', boneMap: [], locals: [] },
    m249: { url: '/cs/models/p_m249.mdl', boneMap: [], locals: [] },
    glock: { url: '/cs/models/p_glock18.mdl', boneMap: [], locals: [] },
    usp: { url: '/cs/models/p_usp.mdl', boneMap: [], locals: [] },
    deagle: { url: '/cs/models/p_deagle.mdl', boneMap: [], locals: [] },
    p228: { url: '/cs/models/p_p228.mdl', boneMap: [], locals: [] },
    elite: { url: '/cs/models/p_elite.mdl', boneMap: [], locals: [] },
    fiveseven: { url: '/cs/models/p_fiveseven.mdl', boneMap: [], locals: [] },
  }
  private active: WeaponKind = 'knife'

  constructor() {
    const scene = Experience.instance.scene
    this.group.visible = false
    this.group.add(this.inner)
    scene.add(this.group)

    // The world is unlit (lightmaps on MeshBasicMaterial). Add lights so the
    // model's StandardMaterials are shaded; the map's basic materials ignore them.
    if (!sharedLightsAdded) {
      const ambient = new THREE.AmbientLight(0xffffff, 2.15)
      const hemi = new THREE.HemisphereLight(0xf4e5c8, 0x7a6c58, 1.1)
      const key = new THREE.DirectionalLight(0xfff2dc, 2.2)
      key.position.set(0.4, 1, 0.3)
      const rim = new THREE.DirectionalLight(0xb6d5ff, 0.7)
      rim.position.set(-0.7, 0.55, -0.45)
      scene.add(ambient, hemi, key, rim)
      sharedLightsAdded = true
    }

    void this.load()
  }

  private async load(): Promise<void> {
    try {
      const res = await fetch(MODEL_URL)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const mdl = parseMdl(await res.arrayBuffer())
      const built = buildMdlMesh(mdl)

      this.gait = {
        idle: findSequence(mdl, 'idle'),
        walk: findSequence(mdl, 'walk', 'run'),
        run: findSequence(mdl, 'run', 'walk'),
        crouchIdle: findSequence(mdl, 'crouch_idle', 'crouchidle', 'duck'),
        crouchWalk: findSequence(mdl, 'crouchrun', 'crouch_walk', 'crouch_idle'),
      }
      this.deaths = [
        findSequence(mdl, 'death1'),
        findSequence(mdl, 'death2'),
        findSequence(mdl, 'death3'),
        findSequence(mdl, 'head'),
        findSequence(mdl, 'gutshot'),
        findSequence(mdl, 'back'),
        findSequence(mdl, 'forward'),
        findSequence(mdl, 'crouch_die'),
      ].filter((seq, i, all) => seq >= 0 && all.indexOf(seq) === i)
      const gunAim: AimSet = {
        aim: findSequence(mdl, 'ref_aim_ak47', 'ref_aim', 'aim'),
        shoot: findSequence(mdl, 'ref_shoot_ak47', 'ref_shoot', 'shoot'),
        reload: findSequence(mdl, 'ref_reload_ak47', 'ref_reload', 'reload'),
        crouchAim: findSequence(mdl, 'crouch_aim_ak47', 'ref_aim_ak47'),
        crouchShoot: findSequence(mdl, 'crouch_shoot_ak47', 'ref_shoot_ak47'),
        crouchReload: findSequence(mdl, 'crouch_reload_ak47', 'ref_reload_ak47'),
      }
      this.aimSets = buildDefaultAimSets(gunAim, {
        aim: findSequence(mdl, 'ref_aim_knife', 'ref_aim', 'aim'),
        shoot: findSequence(mdl, 'ref_shoot_knife', 'ref_shoot', 'shoot'),
        reload: -1,
        crouchAim: findSequence(mdl, 'crouch_aim_knife', 'ref_aim_knife'),
        crouchShoot: findSequence(mdl, 'crouch_shoot_knife', 'ref_shoot_knife'),
        crouchReload: -1,
      })
      this.footBones = mdl.bones
        .map((b, i) => ({ i, n: b.name.toLowerCase() }))
        .filter((b) => / foot| toe/.test(b.n))
        .map((b) => b.i)
      // Upper body (torso + arms + head) holds the weapon (aim pose); legs walk.
      this.upperBody = mdl.bones.map((b) =>
        /spine|neck|head|clavicle|arm|forearm|hand|finger|elbow/.test(
          b.name.toLowerCase(),
        ),
      )

      // Pose to idle and sit the feet on the ground.
      built.skin(computeBoneWorld(mdl, sampleBoneLocals(mdl, this.gait.idle, 0)))
      const box = new THREE.Box3().setFromObject(built.group)
      this.inner.position.y = -box.min.y
      this.inner.add(built.group)

      this.mdl = mdl
      this.built = built
      console.info('[cs] player model loaded (leet.mdl) — animated')

      await Promise.all(
        (Object.keys(this.weapons) as WeaponKind[]).map((kind) =>
          this.loadWeapon(mdl, kind),
        ),
      )
      this.applyActiveWeaponVisibility()
    } catch (error) {
      console.warn('[cs] player model failed to load:', error)
    }
  }

  /** Bone-merge a p_*.mdl: shared bone names reuse the player's matrices, so the
   * weapon follows the hand; the weapon's own tip bones add their local offset. */
  private async loadWeapon(player: Mdl, kind: WeaponKind): Promise<void> {
    const slot = this.weapons[kind]
    try {
      const res = await fetch(slot.url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const gmdl = parseMdl(await res.arrayBuffer())
      const gb = buildMdlMesh(gmdl)

      slot.boneMap = gmdl.bones.map((gbone) =>
        player.bones.findIndex((pb) => pb.name === gbone.name),
      )
      slot.locals = sampleBoneLocals(gmdl, -1, 0) // bind pose (tip bones)
      slot.mdl = gmdl
      slot.built = gb
      this.inner.add(gb.group)
      console.info(`[cs] weapon bone-merged (${slot.url})`)
    } catch (error) {
      console.warn(`[cs] weapon model failed to load (${slot.url}):`, error)
    }
  }

  private applyActiveWeaponVisibility(): void {
    for (const kind of Object.keys(this.weapons) as WeaponKind[]) {
      const built = this.weapons[kind].built
      if (built) built.group.visible = kind === this.active
    }
  }

  /** Swap the held weapon (and its third-person fire/aim pose). */
  public setWeapon(kind: WeaponKind): void {
    this.active = kind
    this.applyActiveWeaponVisibility()
  }

  /** Weapon bone world matrices: shared bones from the player, tip bones local. */
  private weaponBoneWorld(
    slot: MergedWeapon,
    playerWorld: THREE.Matrix4[],
  ): THREE.Matrix4[] {
    const gmdl = slot.mdl!
    const world: THREE.Matrix4[] = new Array(gmdl.bones.length)
    const p = new THREE.Vector3()
    for (let i = 0; i < gmdl.bones.length; i++) {
      const mapped = slot.boneMap[i]
      if (mapped >= 0) {
        world[i] = playerWorld[mapped]
        continue
      }
      const L = slot.locals[i]
      const local = new THREE.Matrix4().compose(
        p.set(L.pos[0], L.pos[1], L.pos[2]),
        angleQuaternion(L.rot[0], L.rot[1], L.rot[2]),
        ONE,
      )
      const parent = gmdl.bones[i].parent
      world[i] = parent >= 0 ? world[parent].clone().multiply(local) : local
    }
    return world
  }

  public setPose(
    feet: THREE.Vector3,
    yaw: number,
    pitch: number,
    duckT: number,
    speed01: number,
    time: number,
    action: 'aim' | 'shoot' | 'reload' | 'death' = 'aim',
    actionT = 0,
  ): void {
    this.group.position.copy(feet)
    this.group.rotation.y = yaw + FACE_OFFSET

    if (!this.mdl || !this.built) return

    if (action === 'death' && this.deaths.length > 0) {
      const deathSeq = this.deaths[Math.floor(actionT) % this.deaths.length]
      const seqDef = this.mdl.sequences[deathSeq]
      const frameT = actionT - Math.floor(actionT)
      const frame = Math.min(seqDef.numFrames - 1, frameT * seqDef.numFrames)
      const locals = sampleBoneLocals(this.mdl, deathSeq, frame)
      const root = this.mdl.bones[0]
      locals[0].pos[0] = root.pos[0]
      locals[0].pos[1] = root.pos[1]
      const boneWorld = computeBoneWorld(this.mdl, locals)
      // A fallen body's feet are no longer its lowest point, so ground the
      // corpse by the whole skeleton — otherwise it floats or clips the floor.
      this.groundLowestBone(boneWorld)
      this.built.skin(boneWorld)
      const slot = this.weapons[this.active]
      if (slot.built && slot.mdl) {
        slot.built.skin(this.weaponBoneWorld(slot, boneWorld))
      }
      return
    }

    const ducking = duckT > 0.5
    let seq: number
    if (ducking)
      seq = speed01 > 0.1 ? this.gait.crouchWalk : this.gait.crouchIdle
    else if (speed01 > 0.55) seq = this.gait.run
    else if (speed01 > 0.06) seq = this.gait.walk
    else seq = this.gait.idle

    // Lower body plays the gait (idle/walk/run); upper body holds the aim pose.
    const s = this.mdl.sequences[seq]
    const fps = s.fps > 0 ? s.fps : 10
    const frames = s.numFrames > 1 ? s.numFrames : 1
    const locals = sampleBoneLocals(this.mdl, seq, (time * fps) % frames)

    // Upper body: the active weapon's aim pose, or its shoot/reload sequence.
    const set = this.aimSets[this.active]
    let upperSeq = ducking ? set.crouchAim : set.aim
    let upperFrame = 0
    if (action === 'shoot') {
      upperSeq = ducking ? set.crouchShoot : set.shoot
      upperFrame = actionT * (this.mdl.sequences[upperSeq].numFrames - 1)
    } else if (action === 'reload') {
      const reloadSeq = ducking ? set.crouchReload : set.reload
      if (reloadSeq >= 0) {
        upperSeq = reloadSeq
      }
      upperFrame = actionT * (this.mdl.sequences[upperSeq].numFrames - 1)
    }
    const aimBlend = -THREE.MathUtils.radToDeg(pitch)
    const aim = sampleBoneLocals(this.mdl, upperSeq, upperFrame, aimBlend)
    for (let i = 0; i < locals.length; i++) {
      if (this.upperBody[i]) {
        locals[i].pos = aim[i].pos
        locals[i].rot = aim[i].rot
      }
    }

    // Keep the walk/run cycle in place: drop the root bone's horizontal motion.
    const root = this.mdl.bones[0]
    locals[0].pos[0] = root.pos[0]
    locals[0].pos[1] = root.pos[1]

    const boneWorld = computeBoneWorld(this.mdl, locals)
    this.groundPose(boneWorld)
    this.built.skin(boneWorld)

    // Re-skin the active bone-merged weapon so it follows the hand.
    const slot = this.weapons[this.active]
    if (slot.built && slot.mdl) {
      slot.built.skin(this.weaponBoneWorld(slot, boneWorld))
    }
  }

  private groundPose(boneWorld: THREE.Matrix4[]): void {
    if (this.footBones.length === 0) return
    let minY = Infinity
    const p = new THREE.Vector3()
    for (const i of this.footBones) {
      p.setFromMatrixPosition(boneWorld[i])
      minY = Math.min(minY, p.z) // GoldSrc Z maps to three.js Y.
    }
    if (Number.isFinite(minY)) this.inner.position.y = -minY
  }

  /** Ground by the lowest bone of the whole skeleton (used for death, where the
   * body lies down and the feet are no longer the lowest point). */
  private groundLowestBone(boneWorld: THREE.Matrix4[]): void {
    let minY = Infinity
    const p = new THREE.Vector3()
    for (const m of boneWorld) {
      p.setFromMatrixPosition(m)
      if (p.z < minY) minY = p.z // GoldSrc Z maps to three.js Y.
    }
    if (Number.isFinite(minY)) this.inner.position.y = -minY
  }

  public setVisible(visible: boolean): void {
    this.group.visible = visible
  }

  public dispose(): void {
    this.group.removeFromParent()
  }
}

function buildDefaultAimSets(
  gun: AimSet = DEFAULT_AIM_SET,
  knife: AimSet = DEFAULT_KNIFE_AIM_SET,
): Record<WeaponKind, AimSet> {
  return {
    knife,
    ak: gun,
    mp5: gun,
    tmp: gun,
    p90: gun,
    mac10: gun,
    m4a1: gun,
    m3: gun,
    xm1014: gun,
    m249: gun,
    glock: gun,
    usp: gun,
    deagle: gun,
    p228: gun,
    elite: gun,
    fiveseven: gun,
  }
}
